import { formatCartPrice } from '../lib/cart-formatters';
import type { CartCurrency, CartItem, CartSummary } from '../types/cart';
import { CartPriceText } from './CartPriceText';

interface CartSummaryRowProps {
  label: string;
  value: string;
  tone?: 'default' | 'discount';
}

const CartSummaryRow = ({ label, value, tone = 'default' }: CartSummaryRowProps) => (
  <div className="flex items-baseline justify-between">
    <dt
      className={
        tone === 'discount' ? 'text-adipa-success font-medium' : 'text-adipa-text-secondary'
      }
    >
      {label}
    </dt>
    <dd
      className={
        tone === 'discount'
          ? 'text-adipa-success font-semibold'
          : 'text-adipa-text-primary font-medium'
      }
    >
      {value}
    </dd>
  </div>
);

const CartSheetTotalRow = ({ amount, currency }: { amount: number; currency: CartCurrency }) => (
  <div className="flex items-baseline justify-between">
    <dt className="text-adipa-text-secondary text-sm font-medium">Total a pagar</dt>
    <dd>
      <CartPriceText amount={amount} currency={currency} size="total" />
    </dd>
  </div>
);

/** Mismas filas y orden que el resumen de compra del legacy y del checkout nuevo. */
export const CartSheetSummary = ({ summary }: { summary: CartSummary }) => {
  const money = (amount: number) => formatCartPrice(amount, summary.currency);
  const hasCouponDiscount = summary.coupons.length > 0 || summary.couponDiscountAmount > 0;
  const couponPercent =
    summary.couponDiscountPercent > 0 ? ` (-${summary.couponDiscountPercent}%)` : '';

  return (
    <dl className="mb-3 space-y-1.5 text-sm">
      <CartSummaryRow label="Precio programas" value={money(summary.programsAmount)} />
      {summary.programsDiscountAmount > 0 ? (
        <CartSummaryRow
          label="Descuento de programas"
          tone="discount"
          value={money(-summary.programsDiscountAmount)}
        />
      ) : null}
      <CartSummaryRow label="Subtotal" value={money(summary.subtotalAmount)} />
      {hasCouponDiscount ? (
        <CartSummaryRow
          label={`Descuento de cupones${couponPercent}`}
          tone="discount"
          value={money(-summary.couponDiscountAmount)}
        />
      ) : null}
      {summary.coupons.length > 0 ? (
        <CartSummaryRow label="Cupón aplicado" value={summary.coupons.join(', ')} />
      ) : null}
      <div className="border-adipa-border border-t pt-2">
        <CartSheetTotalRow amount={summary.totalAmount} currency={summary.currency} />
      </div>
    </dl>
  );
};

/** Sin desglose del host el sheet suma los ítems: precio tachado contra precio. */
export const CartSheetLocalTotals = ({
  currency,
  items,
}: {
  currency: CartCurrency;
  items: CartItem[];
}) => {
  const subtotal = items.reduce((sum, i) => sum + (i.original_price ?? i.price), 0);
  const total = items.reduce((sum, i) => sum + i.price, 0);
  const savings = subtotal - total;

  if (savings <= 0) {
    return (
      <dl className="mb-3">
        <CartSheetTotalRow amount={total} currency={currency} />
      </dl>
    );
  }

  return (
    <dl className="mb-3 space-y-1.5 text-sm">
      <CartSummaryRow label="Subtotal" value={formatCartPrice(subtotal, currency)} />
      <CartSummaryRow
        label="Ahorro total"
        tone="discount"
        value={formatCartPrice(-savings, currency)}
      />
      <div className="border-adipa-border border-t pt-2">
        <CartSheetTotalRow amount={total} currency={currency} />
      </div>
    </dl>
  );
};
